import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { AuthenticationService } from '../authentication/authentication.service';
import { environment } from '@/environments/environment';

/**
 * Enriches Sentry with per-request performance attributes for RDO API and
 * metrics-api calls. A dedicated child span is created for each intercepted
 * request (op: 'rdo.api.request') so that concurrent requests do not overwrite
 * each other and cache_status is safely written before the span ends.
 *
 * Why not getActiveSpan() + setAttribute():
 *   getActiveSpan() at interceptor time returns the enclosing route/pageload
 *   transaction, which is shared across all concurrent requests. Writing to it
 *   causes last-write-wins for every attribute, and cache_status (set on
 *   response) can arrive after the parent transaction has already ended.
 *   startInactiveSpan() gives each request its own isolated span.
 *
 * Attributes written per request:
 *   client_code        – ClientCode of the selected client at request time.
 *                        Accurate for multiclient users (reflects active selection).
 *   user_email_domain  – Domain portion of the login email. Used in BigQuery as
 *                        a fallback to derive client_code_from_email for data
 *                        collected before this interceptor reached production.
 *   which_api          – "rdo-api" or "metrics-api" derived from request URL.
 *   which_db           – "AlloyDB" or "SQL Server". metrics-api is always
 *                        AlloyDB-backed; for rdo-api the X-Rouse-AlloyDB-Features:
 *                        true request header is the ground truth per request.
 *   cache_status       – "hit" or "miss" from the X-Cache-Status response header
 *                        set by FastCacheCheckFilter in rdo-api.
 *                        Requires X-Cache-Status in Access-Control-Expose-Headers
 *                        on the API side — verify in rdo-api Startup.cs CORS config.
 *
 * Requests to other hosts (Auth0, Firestore, etc.) are passed through untouched.
 */
@Injectable()
export class SentryEnrichmentInterceptor implements HttpInterceptor {
  private readonly rdoApiUrl = environment.apiUrl;
  private readonly metricsApiUrl = environment.metricsApiUrl;

  constructor(private authService: AuthenticationService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const isRdoApi = req.url.startsWith(this.rdoApiUrl);
    const isMetricsApi = req.url.startsWith(this.metricsApiUrl);

    if (!isRdoApi && !isMetricsApi) {
      return next.handle(req);
    }

    const userInfo = this.authService._userInfoView;
    const clientCode = userInfo?.SelectedClient?.ClientCode;
    const email = userInfo?.ImpersonatedOrCurrentUserEmail;
    const domain = email ? email.split('@')[1] : undefined;
    // metrics-api is always AlloyDB-backed, independently of the rdo-api header flag.
    // For rdo-api the per-request X-Rouse-AlloyDB-Features header is the ground truth.
    const isAlloyDB =
      isMetricsApi || req.headers.get('X-Rouse-AlloyDB-Features') === 'true';

    const urlPath = (() => {
      try {
        return new URL(req.url).pathname;
      } catch {
        return req.url;
      }
    })();

    let span: ReturnType<typeof Sentry.startInactiveSpan> | undefined;
    try {
      span = Sentry.startInactiveSpan({
        op: 'rdo.api.request',
        name: `${req.method} ${urlPath}`,
        attributes: {
          ...(clientCode && { client_code: clientCode.toLowerCase() }),
          ...(domain && { user_email_domain: domain.toLowerCase() }),
          which_api: isMetricsApi ? 'metrics-api' : 'rdo-api',
          which_db: isAlloyDB ? 'AlloyDB' : 'SQL Server',
        },
      });
    } catch {
      // Sentry failure must never block the request
    }

    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && span) {
          const cacheStatus = event.headers.get('X-Cache-Status');
          if (cacheStatus) {
            span.setAttribute('cache_status', cacheStatus.toLowerCase());
          }
        }
      }),
      finalize(() => {
        try {
          span?.end();
        } catch {
          // ignore
        }
      })
    );
  }
}
